import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { IngredientLite } from '../../../../../../../../shared/models/ingredient/ingredient-lite.model';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { ApplianceGroup, DataRef, ParameterSettings, Program, Slideshow } from '../../../../../../../../shared/models/dataref.model';
import { RefDataService } from '../../../../../../../../services/ref-data.service';
import { StepLite } from '../../../../../../../../shared/models/recipe/step-lite.model';
import { CdkDragSortEvent, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';
import { BinariesService } from '../../../../../../../../services/binaries/binaries.service';
import { RecipeLite } from '../../../../../../../../shared/models/recipe/recipe-lite.model';

@Component({
  selector: 'app-steps-block',
  templateUrl: './steps-block.component.html',
  styleUrls: ['./steps-block.component.scss'],
  standalone: false,
})
export class StepsBlockComponent implements OnInit, OnChanges, OnDestroy {
  @Input() variant?: RecipeLite | null;
  @Input() formArray?: UntypedFormArray;
  @Input() formVariant?: UntypedFormGroup;
  @Output() addLine = new EventEmitter<StepLite | undefined>();
  @Input() kitchenwaresDataref?: DataRef[] = [];
  @Input() utensilDataref?: DataRef[] = [];
  @Input() techniqueDataref?: DataRef[] = [];
  @Input() stepTypesDataref?: DataRef[] = [];
  @Input() programList?: Program[] = [];
  @Input() parameterSettingsList?: ParameterSettings[] = [];
  @Input() psNamesList?: DataRef[] = [];
  @Input() applianceGroupsList?: ApplianceGroup[] = [];
  @Input() slideshows?: Slideshow[] = [];
  @Input() updateComponent?: boolean;
  @Input() domain = '';
  @Input() lang = '';
  @Input() market = '';

  @ViewChildren('stepLineNameField') stepLineNameFields?: QueryList<ElementRef<HTMLInputElement>>;

  formGroups?: UntypedFormGroup[];
  lineAdded = false;
  kitchenwares: string[] = [];
  ingredients: IngredientLite[] = [];
  panelProgrammesState = false;
  warningsApplianceDescription: boolean[] = [];
  private ingredientSubscription: Subscription | undefined;
  private kitchenwareSubscription: Subscription | undefined;

  constructor(
    protected refDataService: RefDataService,
    private binariesService: BinariesService,
  ) {}

  updateUtensil(): void {
    let utensilList: string[] = []; // update the list in root of recipe object
    this.formArray?.controls.forEach((value) => (utensilList = [...utensilList, ...(value.value.utensils ?? [])]));
  }

  updateTechnique(): void {
    let techniqueList: string[] = []; // update the list in root of recipe object
    this.formArray?.controls.forEach((value) => (techniqueList = [...techniqueList, ...(value.value.techniques ?? [])]));
    this.formVariant?.controls.techniques.setValue(Array.from(new Set(techniqueList)));
  }

  ngOnInit(): void {
    if (this.formArray) {
      this.formGroups = this.formArray.controls as UntypedFormGroup[];
    }
    if (this.formGroups) {
      this.formGroups.forEach((formGroup: UntypedFormGroup, index: number) => {
        this.checkDescriptionSize(formGroup, index);
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.formArray && this.formArray) {
      this.formGroups = this.formArray.controls as UntypedFormGroup[];
    }
    if (changes.formVariant?.currentValue) {
      this.kitchenwares = changes.formVariant.currentValue.controls.kitchenwares.value;
      this.ingredients = changes.formVariant.currentValue.controls.ingredients.value;
      this.ingredients = this.ingredients.filter((ingredient) => ingredient.applicationDescription?.trim() !== ''); // remove empty
      this.ingredientSubscription?.unsubscribe();
      this.ingredientSubscription = changes.formVariant.currentValue.controls.ingredients.valueChanges.subscribe(
        (ingredients: IngredientLite[]) => {
          // update ingredient list
          this.ingredients = ingredients;
          this.ingredients = this.ingredients.filter((ingredient) => ingredient.applicationDescription.trim() !== ''); // remove empty
          const ingredientlistOfKey = this.formVariant?.controls.ingredients.value.map((ing: IngredientLite) => ing.identifier);
          this.formArray?.controls.forEach((value) => {
            // remove deleted ingredient
            const step = value.value;
            step.ingredients = step.ingredients?.filter((ingredientsOfStep: string) => ingredientlistOfKey.includes(ingredientsOfStep));
            value.patchValue(step);
          });
        },
      );
      this.kitchenwareSubscription?.unsubscribe();
      this.kitchenwareSubscription = changes.formVariant.currentValue.controls.kitchenwares.valueChanges.subscribe(
        (kitchenwares: string[]) => {
          // update kitchenware list
          this.kitchenwares = kitchenwares;
          this.formArray?.controls.forEach((value) => {
            // remove deleted kitchenware
            const step = value.value;
            step.kitchenwares = step.kitchenwares?.filter((ingredientsOfStep: string) => this.kitchenwares.includes(ingredientsOfStep));
            value.patchValue(step);
          });
        },
      );
    }
  }

  ngOnDestroy(): void {
    this.ingredientSubscription?.unsubscribe();
    this.kitchenwareSubscription?.unsubscribe();
  }

  fastInput(event: ClipboardEvent, index: number): void {
    const pastedData = event.clipboardData?.getData('text/plain');
    const newSteps = pastedData?.trim().split('\n');
    if (newSteps && newSteps.length > 1) {
      // Prevent paste on the clicked line
      event.preventDefault();
      event.stopPropagation();
      newSteps.forEach((stepDescription: string, indexStep: number) => {
        const form = this.formArray?.controls[index] as UntypedFormGroup;
        if (
          indexStep === 0 ||
          (form !== undefined && (form.controls.stepDescription?.value === null || form.controls.stepDescription?.value === ''))
        ) {
          form.controls.stepDescription?.patchValue(stepDescription);
        } else {
          // add
          this.addLine.emit({ index, stepDescription } as any);
        }
        index++;
      });
      this.lineAdded = true;
    }
  }

  focusNextLine(index: number): void {
    if (index === (this.stepLineNameFields?.length ?? 0) - 1) {
      this.newLine();
      return;
    }
    this.stepLineNameFields?.get(index + 1)?.nativeElement?.focus();
  }

  getName(key: string, list: DataRef[] | undefined): string {
    const obj: any = (list ?? []).find((object: any) => object.key === key);
    if (obj) {
      return obj.translatedName;
    }
    // Placeholder for the kitchenware, obj should never be undefined
    // The refresh of the mat-option doesn't occurs correctly if there is no placeholder
    // The flow of the changes in the step screen should be reworked to avoid this behaviour
    return 'Undefined';
  }
  newLine(): void {
    this.lineAdded = true;
    this.addLine.emit();
  }

  reorder(event: CdkDragSortEvent<UntypedFormGroup[]>): void {
    if (event.previousIndex !== event.currentIndex && this.formArray) {
      moveItemInArray(this.formArray.controls, event.previousIndex, event.currentIndex);
      this.formGroups = this.formArray.controls as UntypedFormGroup[];
      this.formArray.markAsDirty();
      this.formArray.updateValueAndValidity();
    }
  }

  delete(index: number): void {
    if (this.formArray) {
      this.formArray.removeAt(index);
      this.formArray.markAsDirty();
      this.formGroups = this.formArray.controls as UntypedFormGroup[];
    }
  }

  checkDescriptionSize(formGroup: UntypedFormGroup, index: number): void {
    if (formGroup.value.type !== 'PREPARATION') {
      this.binariesService
        .isFieldLengthValid(
          this.market,
          this.lang,
          'applianceDescription',
          formGroup.controls.applianceDescription.value,
          formGroup.value.type,
        )
        .subscribe((isValid: any) => {
          this.warningsApplianceDescription[index] = !isValid;
        });
    } else {
      this.warningsApplianceDescription[index] = false;
    }
  }
}
